import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import { Box, Menu, MenuItem } from "@mui/material";
import { type FC, useMemo } from "react";
import { SystemReactTinyPopover } from "@/ui/Popups/SystemReactTinyPopover";
import { WarningDialog } from "@/utils/warningDialogs/WarningDialog";
import { NurseMenu } from "../nurseContainer/NurseMenu";
import { EditHoursAndLocation } from "./EditHoursAndLocation";
import type { NormalizedNurseMenuProps } from "./NormalizedNurseMenuTypes";
import { useNormalizedNurseMenu } from "./useNormalizedNurseMenu";

/**
 * NormalizedNurseMenu component - This component normalizes the usage of NurseMenu and NurseReasonMenu and also exports a reusable state hook
 * @param menuAnchorElement - The anchor element for the menu(s)
 * @param nurseInShift - The nurse in shift data
 * @param source - The source of the changes
 * @param onClose - Callback function when the nurse menu is closing - this is not used for the reason menu
 * @param displayProfileButton - Optional prop to display the profile button in the menu
 * @param allowEditLocationAndHours
 * @param currentQuickAction - The current quick action being performed
 * @param showActual - Whether the component is showing actual data
 * @param handleDeclineChanges - Optional callback function to handle declining changes
 * @param handleAcceptChanges - Optional callback function to handle accepting changes
 * @param onSwapClick - Optional callback function to handle swap click
 */
export const NormalizedNurseMenu: FC<NormalizedNurseMenuProps> = ({
	menuAnchorElement,
	nurseInShift,
	source,
	onClose,
	displayProfileButton,
	allowEditLocationAndHours = true,
	currentQuickAction,
	showActual,
	handleDeclineChanges,
	handleAcceptChanges,
	onSwapClick,
}) => {
	const {
		updateNurseLocationOrTimesLoading,
		handleSaveLocationAndHours,
		editLocationAndHoursOpen,
		handleCloseNurseMenu,
		handleOpenEditLocationAndHoursClick,
		handleCloseEditLocationAndHoursClick,
		setNestedPopoverOpen,
		warningState,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		longShiftDuration,
	} = useNormalizedNurseMenu({
		nurseInShift,
		onClose,
		currentQuickAction,
	});

	const editHoursAndLocationPopover = useMemo(
		() => (
			<SystemReactTinyPopover
				positions={["right", "left", "top", "bottom"]}
				content={
					<EditHoursAndLocation
						onNestedPopoverChange={(isOpen) => {
							setNestedPopoverOpen(isOpen);
						}}
						isLoading={updateNurseLocationOrTimesLoading}
						nurseInShift={nurseInShift}
						onSave={handleSaveLocationAndHours}
					/>
				}
				isOpen={editLocationAndHoursOpen}
				onClose={handleCloseEditLocationAndHoursClick}
				containerStyle={{
					zIndex: "1301" /*Make it work on top of mui backdrop*/,
				}}
			>
				{currentQuickAction ? (
					<div />
				) : (
					<MenuItem key={"edit-hours-location"} onClick={handleOpenEditLocationAndHoursClick}>
						<EditLocationAltOutlinedIcon fontSize={"small"} sx={{ marginRight: "8px" }} />
						Edit Shift Details
					</MenuItem>
				)}
			</SystemReactTinyPopover>
		),
		[
			editLocationAndHoursOpen,
			handleOpenEditLocationAndHoursClick,
			handleCloseEditLocationAndHoursClick,
			nurseInShift,
			updateNurseLocationOrTimesLoading,
			handleSaveLocationAndHours,
			setNestedPopoverOpen,
			currentQuickAction,
		],
	);

	return (
		<Box
			sx={{
				position: "absolute",
			}}
		>
			<WarningDialog
				openPtoWarning={warningState.isPtoWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				longShiftDuration={longShiftDuration}
				onCancelPto={handleCancelWarnings}
				onConfirmPto={handlePtoConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
			/>
			{currentQuickAction && (
				<Menu
					open={true}
					anchorEl={menuAnchorElement}
					anchorOrigin={{
						vertical: "bottom",
						horizontal: "right",
					}}
					transformOrigin={{
						vertical: "top",
						horizontal: "right",
					}}
					slotProps={{
						paper: {
							sx: {
								background: "transparent",
								boxShadow: "none",
							},
						},
					}}
				>
					{editHoursAndLocationPopover}
				</Menu>
			)}
			{menuAnchorElement && (
				<NurseMenu
					nurseInShift={nurseInShift}
					handleAcceptChanges={handleAcceptChanges}
					onSwapClick={onSwapClick}
					handleDeclineChanges={handleDeclineChanges}
					actionsMenuAnchorEl={menuAnchorElement}
					onMenuClose={handleCloseNurseMenu}
					source={source}
					displayProfileButton={displayProfileButton}
					allowEditLocationAndHoursMenuItem={allowEditLocationAndHours ? editHoursAndLocationPopover : undefined}
					currentQuickAction={currentQuickAction}
					showActual={showActual}
				/>
			)}
		</Box>
	);
};
